import type {
  DiscoverClientSource,
  DiscoverEvidenceBlock,
  DiscoverNoteSource,
} from "../shared/source-contract";
import { GenericEvidenceCard } from "./generic-evidence-card";
import { redditClientSource } from "./reddit-client-source";
import { rssClientSource } from "./rss-client-source";
import { twitterClientSource } from "./twitter-client-source";

const discoverClientSources = [
  redditClientSource,
  twitterClientSource,
  rssClientSource,
] satisfies readonly DiscoverClientSource[];

export function getDiscoverClientSource({
  metadata,
  sourceId,
  sourceType,
}: {
  sourceType: string;
  sourceId: string;
  metadata: Record<string, unknown>;
}): DiscoverClientSource | null {
  return (
    discoverClientSources.find((source) =>
      source.matches({
        sourceType,
        sourceId,
        metadata,
      })
    ) ?? null
  );
}

/** Source IDs that should not render as evidence embeds in stories. */
const HIDDEN_EMBED_SOURCES = new Set<string>();

export function shouldHideDiscoverEvidenceEmbed({
  metadata,
  sourceId,
  sourceType,
}: {
  sourceType: string;
  sourceId: string;
  metadata: Record<string, unknown>;
}): boolean {
  const source = getDiscoverClientSource({
    sourceType,
    sourceId,
    metadata,
  });

  return source ? HIDDEN_EMBED_SOURCES.has(source.id) : false;
}

export function renderDiscoverEvidenceBlock({
  block,
  key,
}: {
  block: DiscoverEvidenceBlock;
  key: string;
}) {
  if (
    block.placement !== "appendix" &&
    shouldHideDiscoverEvidenceEmbed({
      sourceType: block.sourceType,
      sourceId: block.sourceId,
      metadata: block.metadata,
    })
  ) {
    return null;
  }

  const source = getDiscoverClientSource({
    sourceType: block.sourceType,
    sourceId: block.sourceId,
    metadata: block.metadata,
  });

  if (!source) {
    return <GenericEvidenceCard block={block} key={key} />;
  }

  return source.renderEvidence({
    block,
    key,
  });
}

export function renderDiscoverNoteSource({
  exploreBase,
  note,
  onOpenChange,
}: {
  exploreBase: string;
  note: DiscoverNoteSource;
  onOpenChange: (open: boolean) => void;
}) {
  const source = getDiscoverClientSource({
    sourceType: note.sourceType,
    sourceId: note.sourceId,
    metadata: {},
  });

  if (!source?.renderNoteSource) {
    return null;
  }

  return source.renderNoteSource({
    exploreBase,
    note,
    onOpenChange,
  });
}

export function resolveDiscoverAppendixIdentity({
  metadata,
  sourceId,
  sourceType,
}: {
  sourceType: string;
  sourceId: string;
  metadata: Record<string, unknown>;
}) {
  const source = getDiscoverClientSource({
    sourceType,
    sourceId,
    metadata,
  });

  if (!source?.resolveAppendixIdentity) {
    return null;
  }

  return source.resolveAppendixIdentity({
    sourceType,
    sourceId,
    metadata,
  });
}
